import type { input, output } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid, Stack } from '@mui/material';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import FormNumberInput from '../../molecules/FormNumberInput';
import FormSelect from '../../molecules/FormSelect';
import { CommissionConfig, CommissionFieldConfig, CommissionFormData } from './commissionFormConfig';
import { commissionSchema } from '../../../schema';
import { styles } from './styles';
import CommissionPerPickTable from './CommissionPerPickTable';

import { mergeSx } from '../../../utils/styles';

type CommissionFormProps = {
  config: CommissionConfig;
  initialData?: CommissionFormData;
  handleSave: SubmitHandler<CommissionFormData>;
  disabled?: boolean;
};

const CommissionForm = ({ config, initialData, handleSave, disabled = false }: CommissionFormProps) => {
  const {
    control,
    watch,
    reset,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<input<typeof commissionSchema>, unknown, output<typeof commissionSchema>>({
    defaultValues: {
      ...config.defaultValues,
      ...initialData,
      picks: { ...config.defaultValues.picks, ...initialData?.picks },
    },
    resolver: zodResolver(commissionSchema),
  });

  const formData = watch();

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  const renderField = (fieldConfig: CommissionFieldConfig): React.ReactNode => {
    const { type, name, label, gridSize, options, nestedFields, allowDecimals, displayCurrency } = fieldConfig;

    const renderInput = () => {
      if (type === 'picks-table') {
        return <CommissionPerPickTable control={control} errors={errors} disabled={disabled} />;
      }

      if (type === 'select') {
        return (
          <FormSelect
            name={name}
            label={label}
            control={control}
            options={options || []}
            disabled={disabled}
            sx={styles.input}
          />
        );
      }

      return (
        <FormNumberInput
          control={control}
          name={name}
          label={label}
          allowDecimals={allowDecimals}
          displayCurrency={displayCurrency}
          valueAsString
          disabled={disabled}
          sx={styles.inputWithoutPadding}
        />
      );
    };

    // Render nested fields (if any)
    const selectedValue = formData[name];
    const nested = nestedFields?.[selectedValue as string] || [];

    return (
      <>
        <Grid size={gridSize}>{renderInput()}</Grid>

        {nested.map((nf) => (
          <React.Fragment key={nf.name}>{renderField(nf)}</React.Fragment>
        ))}
      </>
    );
  };

  return (
    <Grid
      container
      spacing={2}
      sx={mergeSx(
        {
          mt: 1,
        },
        styles.gridContainer
      )}
    >
      {config.fields.map(renderField)}
      {isDirty && !disabled && (
        <Grid size={12}>
          <Stack
            direction='row'
            spacing={1}
            sx={{
              mt: 2,
              justifyContent: 'flex-end',
            }}
          >
            <Button variant='outlined' onClick={() => reset(initialData)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit(handleSave)}>Save</Button>
          </Stack>
        </Grid>
      )}
    </Grid>
  );
};

export default CommissionForm;
